// src/constants/jsonld-utils.ts
// ============================================================================
// JSON-LD utils — normalize, sanitize, absolutize @id, and safely serialize.
// Fixes:
// 1) Broken JSON-LD when mixing { @context, @graph } objects with plain nodes.
//    -> We always output ONE valid JSON object: { @context, @graph:[...] }.
// 2) FAQPage mainEntity shape issues.
//    -> We sanitize FAQPage.mainEntity into valid Question/Answer objects.
// ============================================================================

const SCHEMA_CONTEXT = 'https://schema.org';
const cleanText = (s: any) => String(s ?? '').replace(/\s+/g, ' ').trim();

const withoutUndefinedDeep = (input: any): any => {
  if (Array.isArray(input)) {
    return input
      .map(withoutUndefinedDeep)
      .filter((v) => v !== undefined && v !== null);
  }
  if (isObj(input)) {
    const out: AnyObj = {};
    for (const [k, v] of Object.entries(input)) {
      const vv = withoutUndefinedDeep(v);
      if (vv === undefined || vv === null) continue;
      out[k] = vv;
    }
    return out;
  }
  return input === undefined ? undefined : input;
};

type AnyObj = Record<string, any>;

const isObj = (x: any): x is AnyObj => !!x && typeof x === 'object' && !Array.isArray(x);
const isStr = (x: any): x is string => typeof x === 'string' && x.trim().length > 0;

const ensureTrailingSlash = (u: string) => (u.endsWith('/') ? u : `${u}/`);

const safeCanonicalBase = (canonicalAbs: string) => {
  const s = String(canonicalAbs || '').trim();
  if (!s) return '';
  const canon = ensureTrailingSlash(s);
  return canon.split('#')[0];
};

/* ─────────────────────────────────────────────────────────────
   FAQPage sanitization
   ───────────────────────────────────────────────────────────── */

function sanitizeFaqNode(node: AnyObj): AnyObj {
  // Accept both FAQPage and objects that include FAQPage in @type array (rare)
  const t = node['@type'];
  const isFaq =
    t === 'FAQPage' ||
    (Array.isArray(t) && t.includes('FAQPage')) ||
    (typeof t === 'string' && t.split(/\s+/).includes('FAQPage'));

  if (!isFaq) return node;

  const rawMain = (node as any).mainEntity;
  const arr = Array.isArray(rawMain) ? rawMain : rawMain ? [rawMain] : [];

  const mainEntity = arr
    .map((q: any) => {
      if (!isObj(q)) return null;

      const qName = cleanText(q.name ?? q.question ?? q.q);
      if (!isStr(qName)) return null;

      const ansRaw = q.acceptedAnswer ?? q.answer ?? q.a;
      const ansObj = isObj(ansRaw) ? ansRaw : { text: ansRaw };

      const aText = cleanText(ansObj.text ?? ansObj.answerText ?? ansObj.a);
      if (!isStr(aText)) return null;

      return {
        '@type': 'Question',
        name: qName,
        acceptedAnswer: {
          '@type': 'Answer',
          text: aText,
        },
      };
    })
    .filter(Boolean);

  return {
    ...node,
    '@type': 'FAQPage',
    mainEntity,
  };
}

/* ─────────────────────────────────────────────────────────────
   JSON-LD normalization (merge graphs safely)
   ───────────────────────────────────────────────────────────── */

function flattenJsonLdInput(input: any): AnyObj[] {
  // Accept: object, array, or null
  if (!input) return [];
  if (Array.isArray(input)) return input.flatMap(flattenJsonLdInput);

  if (!isObj(input)) return [];

  // If it's a graph wrapper -> return its graph nodes (not the wrapper itself)
  if (Array.isArray((input as any)['@graph'])) {
    const graphNodes = (input as any)['@graph'] as any[];
    return graphNodes.flatMap(flattenJsonLdInput);
  }

  return [input];
}

function sanitizeNodes(nodes: AnyObj[]): AnyObj[] {
  return nodes
    .map((n) => sanitizeFaqNode(n))
    .map((n) => {
      // ✅ remove @context inside graph nodes
      if (isObj(n) && '@context' in n) {
        const { '@context': _ctx, ...rest } = n as any;
        return rest as AnyObj;
      }
      return n;
    })
    .map((n) => withoutUndefinedDeep(n))
    .filter((n) => isObj(n));
}

function mergeById(nodes: AnyObj[]): AnyObj[] {
  const map = new Map<string, AnyObj>();
  const ordered: AnyObj[] = [];

  for (const n of nodes) {
    const id = isStr(n['@id']) ? String(n['@id']) : '';
    if (!id) {
      ordered.push(n);
      continue;
    }

    const prev = map.get(id);
    if (!prev) {
      map.set(id, n);
      ordered.push(n);
      continue;
    }

    // ✅ merge shallow, האחרון מנצח
    const merged = { ...prev, ...n };

    // אם @type מתנגש/מוכפל – נאחד בצורה בטוחה
    const t1 = prev['@type'];
    const t2 = n['@type'];
    if (t1 && t2) {
      const arr = Array.from(new Set([].concat(t1 as any, t2 as any).flat().filter(Boolean)));
      if (arr.length === 1) merged['@type'] = arr[0];
      else merged['@type'] = arr;
    }

    map.set(id, merged);

    // לעדכן גם את הרשימה לפי מיקום קיים
    const idx = ordered.findIndex((x) => x && x['@id'] === id);
    if (idx >= 0) ordered[idx] = merged;
  }

  return ordered;
}

function mergeToSingleGraph(input: any): AnyObj {
  const graphNodes = sanitizeNodes(flattenJsonLdInput(input));
  const deduped = mergeById(graphNodes);   // ✅ חדש
  return { '@context': SCHEMA_CONTEXT, '@graph': deduped };
}

/* ─────────────────────────────────────────────────────────────
   Absolutize @id references (#foo -> https://site/page/#foo)
   ───────────────────────────────────────────────────────────── */

   export function absolutizeGraphIds<T = any>(
    node: T,
    canonicalBaseWithSlashNoHash: string
  ): T {
    const base = canonicalBaseWithSlashNoHash;
  
    const LOCAL_URL_KEYS = new Set([
      '@id', 'url', 'sameAs', 'hasMap',
      'isPartOf', 'about', 'mainEntity', 'mainEntityOfPage',
      'provider', 'publisher', 'logo', 'worksFor',
      'itemReviewed', 'aggregateRating', 'breadcrumb',
      'mentions', 'hasPart', 'founder', 'subjectOf', 'target', 'item'
    ]);
  
    const absolutizeStr = (s: string) => (s.startsWith('#') ? `${base}${s}` : s);
  
    const walk = (x: any): any => {
      if (Array.isArray(x)) return x.map(walk);
      if (!x || typeof x !== 'object') return x;
  
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(x)) {
        // string value: "#foo"
        if (LOCAL_URL_KEYS.has(k) && typeof v === 'string') {
          out[k] = absolutizeStr(v);
          continue;
        }
  
        // array of strings/objects
        if (LOCAL_URL_KEYS.has(k) && Array.isArray(v)) {
          out[k] = v.map((vv) => (typeof vv === 'string' ? absolutizeStr(vv) : walk(vv)));
          continue;
        }
  
        // object with @id:"#foo" (נפוץ אצלך)
        if (LOCAL_URL_KEYS.has(k) && v && typeof v === 'object' && typeof (v as any)['@id'] === 'string') {
          out[k] = { ...(v as any), '@id': absolutizeStr((v as any)['@id']) };
          continue;
        }
  
        out[k] = walk(v);
      }
      return out;
    };
  
    return walk(node);
  }

   function safeJsonStringify(data: any): string {
    return JSON.stringify(data)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026');
  }
   
   export function buildJsonLdScriptTag(nodes: any[] | undefined, canonicalAbs?: string) {
    if (!Array.isArray(nodes) || nodes.length === 0) return '';
  
    // 1) Merge everything to one { @context, @graph } object
    const merged = mergeToSingleGraph(nodes);
  
    // 2) Absolutize local "#ids" -> "https://site/page/#id" (optional but recommended)
    const base = canonicalAbs ? safeCanonicalBase(canonicalAbs) : '';
    const finalObj = base ? absolutizeGraphIds(merged, base) : merged;
  
    // 3) Safe stringify (protect from </script>)
    const json = safeJsonStringify(finalObj);
  
    return `<script type="application/ld+json">${json}</script>`;
  }

export type BreadcrumbItem = {
  name: string;
  url: string; // must be ABS + trailing slash if you keep canonical that way
};

export const buildBreadcrumbList = (pageUrl: string, items: BreadcrumbItem[]) => ({
  '@type': 'BreadcrumbList',
  '@id': `${pageUrl}#breadcrumb`,
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: it.url,
  })),
});


export function normalizeGalleryBase(galleryIdAbs: string): string {
  try {
    const u = new URL(String(galleryIdAbs || ''));
    u.hash = '';
    u.search = '';
    if (!u.pathname.endsWith('/')) u.pathname += '/';
    return u.toString();
  } catch {
    const s = String(galleryIdAbs || '').split('#')[0].split('?')[0];
    return s.endsWith('/') ? s : `${s}/`;
  }
}

export function galleryNodeId(galleryIdAbs: string): string {
  return `${normalizeGalleryBase(galleryIdAbs)}#gallery`;
}
